// Legacy patients — the migration workspace for the 2024-26 paper registers.
//
// WHY THIS VIEW EXISTS. 934 patients arrived from the ABEECARE / ENZACARE /
// PALBOCARE spreadsheets in one load. Dropping them into the Today queue as
// 900 open tasks would have buried the live clinical work under a wall of
// triage (the founder's explicit ask: "no historical data on the queue"). So
// the migration gets its OWN room with three sub-queues, and drains itself:
//
//   ACTIVE    on a July-2026 register page, still at triage. The nurse opens
//             the patient, uses the imported hint ("Suggest: Refill — overdue
//             since 12/7"), and re-routes with the normal Set-stage flow. The
//             moment the stage changes, the patient LEAVES this list and joins
//             the ordinary pipeline — no second bookkeeping step, ever.
//   INACTIVE  in the registers before July but not on the July page. Imported
//             on hold; setStage() reactivates on first re-route (see
//             data/cases.js) so working a held patient is one action, not two.
//   ARCHIVED  deceased or switched programme, per the register evidence.
//             Read-only history; every episode is preserved in the workspace.
//
// The header shows how many have already been re-routed — the burn-down that
// tells the founder the migration is actually happening. When Active and
// Inactive both reach zero, this view has done its job and can retire.
//
// legacy_bucket is PROVENANCE and is never cleared: a patient re-routed today
// still carries where they came from, which is what makes the drain countable.

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { register } from '../ui/actions.js';
import { registerView } from './registry.js';
import { loadAllCases, allCases, casesLoaded } from '../data/cases.js';
import { userName, usersMap, loadUsers } from '../data/users.js';
import { resolveStage, isClosedCase } from '../db/contract.js';
import { openWorkspace } from './workspace.js';

/** Search text held across renders (module state, same pattern as patients). */
let _q = '';
/** Collapsed-state per section key; default open for active, closed for rest. */
const _open = { active: true, inactive: false, archived: false };

/**
 * Group the case list into the legacy sub-queues. Pure — unit-tested.
 *
 * A case is IN this view only if it carries a legacy_bucket. Buckets:
 *  - active:   bucket 'active'  AND still at triage AND not closed → to work
 *  - inactive: bucket 'inactive' AND still at triage AND on hold   → parked
 *  - archived: bucket 'archived'                                    → history
 *  - rerouted: bucket active/inactive whose stage has MOVED — the drain count.
 * @param {any[]} cases
 * @param {(id: string) => string} [nameOf] resolves assigned_to → display name
 * @returns {{active: Record<string, any[]>, inactive: any[], archived: any[], rerouted: number, total: number}}
 */
export function groupLegacy(cases, nameOf) {
  /** @type {Record<string, any[]>} */
  const active = {};
  const inactive = [];
  const archived = [];
  let rerouted = 0;
  let total = 0;
  for (const c of cases || []) {
    if (!c || !c.legacy_bucket) continue;
    total++;
    const atTriage = resolveStage(c) === 0 && !isClosedCase(c);
    if (c.legacy_bucket === 'archived') {
      archived.push(c);
    } else if (c.legacy_bucket === 'active' && atTriage) {
      const nurse = (c.assigned_to && nameOf && nameOf(c.assigned_to)) || 'Unassigned';
      (active[nurse] || (active[nurse] = [])).push(c);
    } else if (c.legacy_bucket === 'inactive' && atTriage && c.program_status === 'on_hold') {
      inactive.push(c);
    } else {
      rerouted++; // stage moved (or hold lifted) — they are in the pipeline now
    }
  }
  return { active, inactive, archived, rerouted, total };
}

/**
 * Case-insensitive match on name / legacy code / reference / phone. Pure.
 * @param {any} c @param {string} q @returns {boolean}
 */
export function legacyMatch(c, q) {
  if (!q) return true;
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return ['patient_name', 'legacy_code', 'reference_number', 'patient_phone', 'physician_name']
    .some((k) => String(c[k] || '').toLowerCase().includes(n));
}

function rowHTML(c, kind) {
  const code = c.legacy_code && c.legacy_code !== 'HIST' ? c.legacy_code : (c.reference_number || '');
  const hint = kind === 'active' ? c.legacy_hint || '' : '';
  const phone = c.patient_phone || '';
  return (
    `<div class="lg-row" role="listitem">` +
    `<div class="lg-main"><b>${esc(c.patient_name || '—')}</b>` +
    `<span class="lg-code">${esc(code)}</span>` +
    (c.age ? `<span class="lg-meta">${esc(String(c.age))} yrs</span>` : '') +
    (phone ? `<span class="lg-meta">${esc(phone)}</span>` : '') +
    `</div>` +
    (hint ? `<div class="lg-hint">${esc(hint)}</div>` : '') +
    (kind === 'archived' && c.archive_reason_code
      ? `<div class="lg-hint mut">${esc(String(c.archive_reason_code).replace(/_/g, ' '))}</div>`
      : '') +
    `<button type="button" class="btn sm${kind === 'active' ? ' pri' : ''}" data-action="lg-open" data-case="${esc(c.case_id)}">Open${kind === 'active' ? ' & re-route' : ''}</button>` +
    `</div>`
  );
}

function sectionHTML(key, title, count, bodyHTML, tone) {
  const open = _open[key];
  return (
    `<section class="lg-sec${tone ? ' ' + tone : ''}">` +
    `<button type="button" class="lg-sec-h" data-action="lg-toggle" data-sec="${key}" aria-expanded="${open}">` +
    `<span class="lg-caret">${open ? '▾' : '▸'}</span> ${esc(title)}` +
    `<span class="lg-count">${count}</span></button>` +
    (open ? `<div class="lg-sec-b" role="list">${bodyHTML || '<p class="lg-empty">Nothing here.</p>'}</div>` : '') +
    `</section>`
  );
}

/**
 * What the view may show for a PATIENTS slice. PURE. Until the first case
 * load has RESOLVED the view is 'loading' — it used to paint "No legacy
 * patients loaded yet — run the legacy-load SQL pack first" over an in-flight
 * fetch, telling the founder the go-live migration had not been run while
 * the rows were still on their way (review R-05).
 * @param {any} slice SLICES.PATIENTS value (may be undefined)
 * @returns {'loading'|'error'|'ready'}
 */
export function legacyViewState(slice) {
  const s = slice || {};
  if (s.loadError) return 'error';
  if (!s.loaded) return 'loading';
  return 'ready';
}

/**
 * Is this view the one currently on screen? Pure — unit-tested.
 *
 * The PATIENTS subscription below fires for ANY case load, including the one
 * the Patients registry or the Board kicks off after the nurse has navigated
 * away from here. Without this check render() paints legacy markup over
 * whichever view is actually mounted and the nurse is stuck on Legacy until
 * they reload the page — on the one screen the whole go-live depends on. Both
 * halves are needed: the route id alone is true for a beat before the new view
 * has swapped the DOM, and the sentinel alone is true while the old markup is
 * still on screen. Same guard patients/board/today already carry.
 * @param {any} ui UI slice
 * @param {any} doc document (or a stub in tests)
 * @returns {boolean}
 */
export function legacyMounted(ui, doc) {
  return !!doc && (ui || {}).view === 'legacy' && !!doc.getElementById('legacy-view');
}

function mounted() {
  return legacyMounted(store.get(SLICES.UI), /** @type {any} */ (globalThis).document);
}

function render() {
  const host = document.getElementById('view');
  if (!host) return;
  const slice = store.get(SLICES.PATIENTS) || {};
  const state = legacyViewState(slice);
  if (state === 'error') {
    host.innerHTML =
      '<header class="view-head" id="legacy-view"><h1>Legacy patients</h1></header>' +
      '<div class="import-banner err">Could not load patients. <button type="button" class="btn sm" data-action="lg-retry">Retry</button></div>';
    return;
  }
  if (state === 'loading' || !casesLoaded()) {
    host.innerHTML =
      '<header class="view-head" id="legacy-view"><h1>Legacy patients</h1></header>' +
      '<p class="view-placeholder" aria-busy="true">Loading patients…</p>';
    return;
  }
  const g = groupLegacy(allCases(), (id) => userName(id));
  const q = _q;
  const filt = (list) => list.filter((c) => legacyMatch(c, q));

  let activeBody = '';
  const nurses = Object.keys(g.active).sort();
  let activeCount = 0;
  for (const n of nurses) {
    const rows = filt(g.active[n]);
    activeCount += rows.length;
    if (!rows.length) continue;
    activeBody +=
      `<div class="lg-nurse"><h3 class="lg-nurse-h">${esc(n)} <span class="lg-count">${rows.length}</span></h3>` +
      rows.map((c) => rowHTML(c, 'active')).join('') +
      '</div>';
  }
  const inactiveRows = filt(g.inactive);
  const archivedRows = filt(g.archived);

  host.innerHTML =
    '<header class="view-head" id="legacy-view"><h1>Legacy patients</h1>' +
    `<p class="view-sub">${g.total ? esc(String(g.rerouted)) + ' of ' + esc(String(g.total)) + ' already re-routed into the live pipeline' : 'No legacy patients loaded yet — run the legacy-load SQL pack first'}</p>` +
    '</header>' +
    `<div class="lg-tools"><input id="lg-search" type="search" placeholder="Search name, TH code, phone…" value="${esc(q)}" aria-label="Search legacy patients"></div>` +
    sectionHTML('active', 'Active — re-route these', activeCount, activeBody, 'act') +
    sectionHTML('inactive', 'Inactive — no recent register activity', inactiveRows.length,
      inactiveRows.map((c) => rowHTML(c, 'inactive')).join(''), '') +
    sectionHTML('archived', 'Archived — deceased / switched (history preserved)', archivedRows.length,
      archivedRows.map((c) => rowHTML(c, 'archived')).join(''), 'arch');

  const inp = document.getElementById('lg-search');
  if (inp) {
    inp.addEventListener('input', () => {
      _q = /** @type {HTMLInputElement} */ (inp).value;
      // re-render but keep focus + caret in the search box
      const pos = /** @type {HTMLInputElement} */ (inp).selectionStart;
      render();
      const again = /** @type {HTMLInputElement|null} */ (document.getElementById('lg-search'));
      if (again) {
        again.focus();
        if (pos !== null) again.setSelectionRange(pos, pos);
      }
    });
  }
}

let _wired = false;
function wireOnce() {
  if (_wired) return;
  _wired = true;
  register('lg-open', (el) => {
    const id = el.getAttribute('data-case');
    if (id) void openWorkspace(id);
  });
  register('lg-toggle', (el) => {
    const k = el.getAttribute('data-sec');
    if (k && k in _open) {
      _open[/** @type {'active'|'inactive'|'archived'} */ (k)] = !_open[k];
      render();
    }
  });
  register('lg-retry', () => {
    // Guarded: a retry that resolves after the nurse has navigated away must
    // not paint Legacy markup over whichever view is on screen now — the
    // exact bug legacyMounted() exists to prevent.
    void loadAllCases().then(() => {
      if (mounted()) render();
    });
  });
  store.subscribe(SLICES.PATIENTS, () => {
    if (mounted()) render();
  });
}

/** Route registration (called from main.js). */
export function registerLegacy() {
  registerView('legacy', {
    render(host) {
      host.innerHTML = '<header class="view-head"><h1>Legacy patients</h1></header><p class="view-placeholder">Loading…</p>';
      wireOnce();
      render();
    },
    async load() {
      // Staff directory first-load (names for the per-nurse groups) runs in
      // parallel with the case fetch — same pattern as the Patients registry;
      // a directory miss only degrades names, never blocks the queue.
      const dir = Object.keys(usersMap()).length ? null : loadUsers();
      const r = await loadAllCases();
      if (dir) await dir;
      return r;
    },
  });
}

/** Test hook: reset module state. */
export function _resetLegacy() {
  _q = '';
  _open.active = true;
  _open.inactive = false;
  _open.archived = false;
}
